import { useContext, useEffect, useState, lazy, Suspense } from "react";
// @ts-ignore CSS is loaded by the bundler; no TypeScript declaration is needed.
import "./App.css";
import { LocationContext } from "./contexts/Location";
import { getAllTrail, getHomepageOverview } from "./utils/api";
import { HomePage } from "./components/HomePage";
import { StatisticsPage } from "./components/session/StatisticsPage";
import { BadgesPage } from "./components/BadgesPage";
import { ManagementPage } from "./components/ManagementPage";
import { useAuth } from "./contexts/AuthContext";
import { LoginPage } from "./components/auth/LoginPage";
import { ProfilePage } from "./components/ProfilePage";
import { DashboardDetail } from "./components/dashboard/DashboardDetail";
import MainLayout from "./components/common/MainLayout"; // Import MainLayout
import { TrailForm } from "./components/trail/TrailForm";
import { TrailDetail } from "./components/trail/TrailDetail";
import { EmptyState } from "./components/common/EmptyState";

import {
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
  Outlet,
} from "react-router-dom";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { Trail } from "./types/trail";
import { ViewModeProvider } from "./contexts/ViewModeContext";

// Lazy-loaded components
const AdminPage = lazy(() => import("./components/AdminPage"));
const TrainerDashboard = lazy(() => import("./components/TrainerDashboard"));
const DashboardsListPage = lazy(() => import("./components/DashboardsListPage"));

/**
 * @function App
 * @description The root component of the application. Handles initial loading, authentication,
 * and sets up the `LocationProvider` and `ViewModeProvider` for the rest of the app.
 */
function App() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    trails,
    setTrails,
    triggerGetTrails,
    setTriggerGetTrails,
    homepageOverview,
    setHomepageOverview,
    activitiesLoaded,
    setActivitiesLoaded,
    setActivitiesLoading,
    activitiesError,
    setActivitiesError,
  } = useContext(LocationContext);

  useEffect(() => {
    setTrails([]);
    setActivitiesLoaded(false);
    setActivitiesLoading(false);
    setHomepageOverview(null);
    setTriggerGetTrails(false);
    setActivitiesError(null);
  }, [setActivitiesError, setActivitiesLoaded, setActivitiesLoading, setHomepageOverview, setTrails, setTriggerGetTrails, user?._id]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getHomepageOverview()
      .then((data) => {
        if (!cancelled) setHomepageOverview(data);
      })
      .catch((error) => console.error("Failed to fetch homepage overview:", error));
    return () => { cancelled = true; };
  }, [user, setHomepageOverview]);

  useEffect(() => {
    if (!user) return;
    const requiresFullActivities = location.pathname.startsWith("/trails") || location.pathname === "/badges";
    if (!requiresFullActivities || (activitiesLoaded && !triggerGetTrails)) return;

    let cancelled = false;
    setActivitiesLoading(true);
    setActivitiesError(null);
    getAllTrail()
      .then((data) => {
        if (cancelled) return;
        setTrails(data);
        setActivitiesLoaded(true);
        if (triggerGetTrails) {
          getHomepageOverview()
            .then(setHomepageOverview)
            .catch((error) => console.error("Failed to refresh homepage overview:", error));
        }
      })
      .catch((error) => {
        if (!cancelled) setActivitiesError(error instanceof Error ? error.message : "Impossible de charger les activités.");
        console.error("Failed to fetch activities:", error);
      })
      .finally(() => {
        if (!cancelled) {
          setActivitiesLoading(false);
          if (triggerGetTrails) setTriggerGetTrails(false);
        }
      });
    return () => { cancelled = true; };
  }, [activitiesLoaded, location.pathname, setActivitiesError, setActivitiesLoaded, setActivitiesLoading, setHomepageOverview, setTrails, setTriggerGetTrails, triggerGetTrails, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <ViewModeProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            {" "}
            {/* Use MainLayout for authenticated routes */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route
              path="/home"
              element={
                <HomePage
                  overview={homepageOverview}
                  onViewTrails={() => navigate("/trails")}
                  onCreateNew={() => navigate("/trails/new")}
                  onViewStatistics={() => navigate("/statistics")}
                  onViewBadges={() => navigate("/badges")}
                />
              }
            />
            <Route
              path="/statistics"
              element={<StatisticsPage />}
            />
            <Route path="/badges" element={activitiesError ? <ActivityLoadError /> : activitiesLoaded ? <BadgesPage trails={trails} /> : <div className="p-6">Chargement des activités...</div>} />
            <Route path="/management" element={<ManagementPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* Admin Routes */}
            <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<div>Loading Admin Page...</div>}>
                    <AdminPage />
                  </Suspense>
                }
              />
            </Route>
            {/* Trainer Routes */}
            <Route element={<ProtectedRoute allowedRoles={["trainer"]} />}>
              <Route
                path="/trainer-dashboard"
                element={
                  <Suspense fallback={<div>Loading Trainer Dashboard...</div>}>
                    <TrainerDashboard />
                  </Suspense>
                }
              />
              <Route
                path="/sessions"
                element={
                  <Suspense fallback={<div>Loading Dashboards List...</div>}>
                    <DashboardsListPage
                      onNavigateToDashboard={(id: string) => navigate(`/sessions/${id}`)}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/sessions/:dashboardId"
                element={
                  <DashboardDetailWrapper
                    onBack={() => navigate("/sessions")}
                  />
                }
              />
            </Route>
            {/* Trail Management Routes */}
            <Route path="/trails" element={<Outlet />}>
              {" "}
              {/* TrailsLayout is now integrated into MainLayout */}
              <Route
                index
                element={<TrailListLanding onCreateNew={() => navigate("/trails/new")} />}
              />
              <Route path="new" element={<TrailFormWrapper />} />
              <Route path=":trailId" element={<TrailDetailWrapper />} />
              <Route path=":trailId/edit" element={<TrailFormWrapper />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/home" replace />} />{" "}
        {/* Catch-all for unknown routes */}
      </Routes>
    </ViewModeProvider>
  );
}

const TrailListLanding = ({ onCreateNew }: { onCreateNew: () => void }) => {
  const { trails, activitiesLoaded, activitiesLoading, activitiesError } = useContext(LocationContext);

  if (activitiesError) return <ActivityLoadError />;
  if (!activitiesLoaded || activitiesLoading) {
    return <div className="p-4 text-center">Chargement des pistes...</div>;
  }

  const latestTrail = trails.reduce<Trail | undefined>((latest, trail) => {
    const latestDate = latest ? new Date(latest.date).getTime() : Number.NEGATIVE_INFINITY;
    const trailDate = new Date(trail.date).getTime();
    return Number.isFinite(trailDate) && trailDate > latestDate ? trail : latest;
  }, undefined);
  const latestTrailId = latestTrail?.id || latestTrail?._id;

  if (latestTrailId) return <Navigate to={`/trails/${latestTrailId}`} replace />;
  return <EmptyState onCreateNew={onCreateNew} />;
};

// Helper components for wrapping pages that need URL parameters
const TrailDetailWrapper = () => {
  const { trailId } = useParams();
  const { trails, setTriggerGetTrails, activitiesLoaded, activitiesLoading, activitiesError } = useContext(LocationContext);
  const navigate = useNavigate();
  const selectedTrail = trails.find((t) => (t.id || t._id) === trailId);

  useEffect(() => {
    if (activitiesLoaded && !selectedTrail) {
      navigate("/trails");
    }
  }, [activitiesLoaded, selectedTrail, navigate]);

  if (activitiesError) return <ActivityLoadError />;
  if (!activitiesLoaded || activitiesLoading) return <div className="p-4 text-center">Chargement des pistes...</div>;
  if (!selectedTrail) {
    return <div className="text-center p-4">Piste introuvable.</div>;
  }

  return (
    <TrailDetail
      trail={selectedTrail}
      onEdit={(trail: Trail) =>
        navigate(`/trails/${trail.id || trail._id}/edit`)
      }
      onDeleteSuccess={() => {
        setTriggerGetTrails(true);
        navigate("/trails");
      }}
    />
  );
};

const TrailFormWrapper = () => {
  const { trailId } = useParams();
  const { trails, setTriggerGetTrails, activitiesLoaded, activitiesLoading, activitiesError } = useContext(LocationContext);
  const navigate = useNavigate();
  const [editingTrail, setEditingTrail] = useState<Trail | undefined>(
    undefined
  );

  useEffect(() => {
    if (trailId && activitiesLoaded) {
      const trailToEdit = trails.find((t) => (t.id || t._id) === trailId);
      setEditingTrail(trailToEdit);
    } else {
      setEditingTrail(undefined);
    }
  }, [activitiesLoaded, trailId, trails]);

  if (trailId && activitiesError) return <ActivityLoadError />;
  if (trailId && (!activitiesLoaded || activitiesLoading)) {
    return <div className="p-4 text-center">Chargement des pistes...</div>;
  }

  return (
    <TrailForm
      trail={editingTrail}
      onSaveSuccess={() => {
        setTriggerGetTrails(true);
        navigate(trailId ? `/trails/${trailId}` : "/trails");
      }}
      onCancel={() => navigate(trailId ? `/trails/${trailId}` : "/trails")}
    />
  );
};

const ActivityLoadError = () => {
  const { activitiesError, setTriggerGetTrails } = useContext(LocationContext);
  return (
    <div className="p-6 text-center" role="alert">
      <p>Impossible de charger les activités. {activitiesError}</p>
      <button className="mt-3 underline" onClick={() => setTriggerGetTrails(true)}>Réessayer</button>
    </div>
  );
};

const DashboardDetailWrapper = ({ onBack }: { onBack: () => void }) => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  return (
    <DashboardDetail
      dashboardId={dashboardId || ""}
      onBack={onBack}
      onNavigateToSession={(sessionId) =>
        navigate(`/sessions/${dashboardId}/detail/${sessionId}`)
      } // Example for session detail route
    />
  );
};

// TrailsLayout is now integrated into MainLayout, so this component is no longer needed
// const TrailsLayout = () => {
//   const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
//   const { trails } = useContext(LocationContext);
//   const navigate = useNavigate();
//   const { trailId } = useParams(); // Get trailId from params for sidebar selection

//   // Determine selectedTrailId for TrailList from URL
//   const selectedTrailIdFromUrl = trailId || null;

//   return (
//     <div className="flex-1 flex overflow-hidden">
//       <div
//         className={`flex-shrink-0 border-r border-border shadow-lg transition-all duration-300 ${
//           isSidebarCollapsed ? "w-16" : "w-80"
//         }`}
//       >
//         <TrailList
//           trails={trails}
//           selectedTrailId={selectedTrailIdFromUrl}
//           onSelectTrail={(id) => navigate(`/trails/${id}`)}
//           onCreateNew={() => navigate('/trails/new')}
//           isCollapsed={isSidebarCollapsed}
//           onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
//         />
//       </div>
//       <div className="flex-1">
//         <Outlet /> {/* Renders nested routes */}
//       </div>
//     </div>
//   );
// };

export default App;
